import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Clock, AlertCircle, ArrowRight, Sparkles } from 'lucide-react';
import { API_BASE_URL } from '../config/api';
import SiteHeader from '../components/SiteHeader';
import Footer from '../components/Footer';
import useAuthModal from '../hooks/useAuthModal';
import { getAuthToken } from '../utils/auth';
import { trackEvent } from '../utils/amplitude';
import {
  extractPlanCodeFromProductName,
  buildPlanHighlights,
  getCadenceFromPlanCode,
  getDurationDaysFromPlanCode,
} from '../utils/planCatalog';

async function fetchCatalogPlansFromApi(token) {
  const response = await fetch(`${API_BASE_URL}/api/payments/plans`, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  const data = await response.json().catch(() => []);
  return { response, data };
}

export default function PaymentResultScreen() {
  const authModal = useAuthModal();
  const location = useLocation();
  const navigate = useNavigate();

  const [catalogPlans, setCatalogPlans] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const purchaseTrackedRef = useRef(false);

  const orderReference = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return (
      params.get('order_reference') ||
      params.get('orderReference') ||
      params.get('order') ||
      localStorage.getItem('lastPaymentOrderReference')
    );
  }, [location.search]);

  const getStatusConfig = (statusData) => {
    if (!statusData) return null;

    const statusValue = statusData.status || statusData.transactionStatus || '';

    if (statusValue === 'approved') {
      return {
        type: 'success',
        icon: CheckCircle,
        title: 'Payment Successful!',
        message: 'Your subscription is now active',
        bgColor: 'bg-emerald-50',
        borderColor: 'border-emerald-200',
        textColor: 'text-emerald-700',
        iconColor: 'text-emerald-500',
      };
    }
    if (statusValue === 'declined') {
      return {
        type: 'error',
        icon: XCircle,
        title: 'Payment Declined',
        message: statusData.reason || 'Your payment was not processed',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        textColor: 'text-red-700',
        iconColor: 'text-red-500',
      };
    }
    if (statusValue === 'created') {
      return {
        type: 'pending',
        icon: Clock,
        title: 'Payment Pending',
        message: 'Waiting for payment confirmation',
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-200',
        textColor: 'text-yellow-700',
        iconColor: 'text-yellow-500',
      };
    }

    return {
      type: 'info',
      icon: AlertCircle,
      title: 'Payment Status',
      message: `Status: ${statusValue}`,
      bgColor: 'bg-gray-50',
      borderColor: 'border-gray-200',
      textColor: 'text-gray-700',
      iconColor: 'text-gray-500',
    };
  };

  const formatDate = (daysToAdd) => {
    const date = new Date();
    date.setDate(date.getDate() + daysToAdd);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatMoney = (amount, currencyCode) => {
    const numericAmount = Number(amount ?? 0);
    const currency = String(currencyCode || 'USD').toUpperCase();

    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: Number.isInteger(numericAmount) ? 0 : 2,
        maximumFractionDigits: 2,
      }).format(numericAmount);
    } catch {
      return `${currency} ${numericAmount}`;
    }
  };

  const fetchStatus = useCallback(async () => {
    if (!orderReference) {
      setError('Order reference not found.');
      setLoading(false);
      return;
    }

    const token = getAuthToken();
    if (!token) {
      setError('Please log in to view payment status.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/payments/status/${orderReference}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(data.detail || data.message || 'Unable to load payment status.');
        setLoading(false);
        return;
      }

      setStatus(data);
    } catch (err) {
      console.error('[PaymentResult] status error:', err);
      setError('Unable to load payment status.');
    } finally {
      setLoading(false);
    }
  }, [orderReference]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    if (purchaseTrackedRef.current) return;
    const statusValue = status?.status || status?.transactionStatus || '';
    if (statusValue !== 'approved') return;
    purchaseTrackedRef.current = true;
    const planCode = extractPlanCodeFromProductName(status.productName);
    const catalogPlan = catalogPlans.find((p) => p.plan_code === planCode);
    trackEvent('purchase', {
      purchase_type: catalogPlan?.name ?? planCode ?? 'unknown',
      purchase_price: catalogPlan?.price_usd ?? status.amount ?? null,
    });
  }, [status, catalogPlans]);

  useEffect(() => {
    let isMounted = true;

    const fetchCatalogPlans = async () => {
      try {
        const token = getAuthToken();
        const { response, data } = await fetchCatalogPlansFromApi(token);
        if (!isMounted) return;

        if (response.ok && Array.isArray(data)) {
          setCatalogPlans(data);
          return;
        }

        setCatalogPlans([]);
      } catch {
        if (!isMounted) return;
        setCatalogPlans([]);
      }
    };

    fetchCatalogPlans();

    return () => {
      isMounted = false;
    };
  }, []);

  const statusConfig = getStatusConfig(status);
  const StatusIcon = statusConfig?.icon;

  const resolvedPlanCode = useMemo(() => {
    if (!status) return null;
    return status.plan_code || extractPlanCodeFromProductName(status.product_name);
  }, [status]);

  const planFromCatalog = useMemo(() => {
    if (!resolvedPlanCode || !catalogPlans.length) return null;
    return catalogPlans.find((plan) => plan.plan_code === resolvedPlanCode) || null;
  }, [catalogPlans, resolvedPlanCode]);

  const planDetails = useMemo(() => {
    if (!planFromCatalog) return null;

    const cadence = getCadenceFromPlanCode(planFromCatalog.plan_code, planFromCatalog.price_usd);
    const durationDays = getDurationDaysFromPlanCode(planFromCatalog.plan_code);
    const features = buildPlanHighlights(planFromCatalog);
    const color = String(planFromCatalog.plan_code || '').includes('pro') || String(planFromCatalog.plan_code || '').includes('executive')
      ? 'orange'
      : 'blue';

    return {
      name: planFromCatalog.plan_name,
      cadence,
      durationDays,
      features,
      color,
    };
  }, [planFromCatalog]);

  return (
    <div className="min-h-screen bg-[#F6F7FB] text-[#1E293B]">
      <SiteHeader authModal={authModal} />

      <main className="max-w-4xl mx-auto px-6 py-12">
        {loading && (
          <div className="bg-white rounded-[32px] p-12 shadow-sm text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Checking payment status...</p>
          </div>
        )}

        {error && !loading && (
          <div className="bg-white rounded-[32px] p-8 shadow-sm space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-red-500" />
              </div>
              <h1 className="text-2xl font-semibold text-gray-900 mb-2">Error</h1>
              <p className="text-gray-600">{error}</p>
            </div>

            <div className="flex justify-center gap-3">
              <button
                type="button"
                onClick={() => navigate('/pricing')}
                className="btn-orange px-6 py-3 rounded-2xl text-sm font-semibold"
              >
                Back to pricing
              </button>
            </div>
          </div>
        )}

        {!loading && !error && status && statusConfig && (
          <div className="space-y-6">
            <div className="bg-white rounded-[32px] p-8 shadow-sm">
              <div className="text-center space-y-4">
                <div className={`w-20 h-20 ${statusConfig.bgColor} rounded-full flex items-center justify-center mx-auto mb-4 animate-scale-in`}>
                  <StatusIcon className={`w-10 h-10 ${statusConfig.iconColor}`} />
                </div>

                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">
                    {statusConfig.title}
                  </h1>
                  <p className="text-lg text-gray-600">
                    {statusConfig.message}
                  </p>
                </div>

                {orderReference && (
                  <div className="inline-block rounded-2xl border border-gray-200 bg-gray-50 px-4 py-2 text-sm">
                    <span className="text-gray-500">Order: </span>
                    <span className="font-mono font-semibold text-gray-800">{orderReference}</span>
                  </div>
                )}
              </div>
            </div>

            {statusConfig.type === 'success' && planDetails && (
              <div className="bg-white rounded-[32px] p-8 shadow-sm space-y-6">
                <div className="flex items-center justify-between pb-6 border-b border-gray-100">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className={`w-5 h-5 ${planDetails.color === 'orange' ? 'text-orange-500' : 'text-blue-500'}`} />
                      <h2 className="text-2xl font-bold text-gray-900">
                        {planDetails.name}
                      </h2>
                    </div>
                    <p className="text-gray-600">{planDetails.cadence}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-gray-900">{formatMoney(status.amount, status.currency)}</p>
                    <p className="text-sm text-gray-500">{status.currency}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                    <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Valid Until</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {planDetails.durationDays ? formatDate(planDetails.durationDays) : 'No expiration'}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                    <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Status</p>
                    <p className="text-lg font-semibold text-emerald-600">Active</p>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">
                    Your Benefits
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {planDetails.features.map((feature, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <CheckCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${planDetails.color === 'orange' ? 'text-orange-500' : 'text-blue-500'}`} />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className={`rounded-2xl ${planDetails.color === 'orange' ? 'bg-orange-50 border-orange-100' : 'bg-blue-50 border-blue-100'} border p-6`}>
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Ready to start?</h3>
                      <p className="text-sm text-gray-600">Begin your interview preparation now</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => navigate('/search')}
                      className="btn-orange px-6 py-3 rounded-2xl text-sm font-semibold flex items-center gap-2 whitespace-nowrap"
                    >
                      Start Interview
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                type="button"
                onClick={fetchStatus}
                className="border border-gray-200 bg-white rounded-2xl px-6 py-3 text-sm font-semibold hover:bg-gray-50 transition-colors"
              >
                Refresh Status
              </button>
              <button
                type="button"
                onClick={() => navigate('/profile')}
                className="border border-gray-200 bg-white rounded-2xl px-6 py-3 text-sm font-semibold hover:bg-gray-50 transition-colors"
              >
                View Profile
              </button>
              {statusConfig.type !== 'success' && (
                <button
                  type="button"
                  onClick={() => navigate('/pricing')}
                  className="border border-gray-200 bg-white rounded-2xl px-6 py-3 text-sm font-semibold hover:bg-gray-50 transition-colors"
                >
                  Back to Pricing
                </button>
              )}
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
